import { observer } from 'mobx-react';
import { Component, ComponentProps } from 'react';

import { ImageData } from '../../data/model';
import { InitialStoreSelectorModalTranslation } from '../../localization/SiteTranslation';
import BackgroundImage from '../../shared/components/BackgroundImage/BackgroundImage';
import BackgroundVideoPlayer from '../../shared/components/BackgroundVideoPlayer/BackgroundVideoPlayer';
import TertiaryButton from '../../shared/components/Button/TertiaryButton';
import { MenuListComponent } from '../../shared/components/CustomSelect/CustomSelect';
import Logo from '../../shared/components/Logo/Logo';
import Modal from '../../shared/components/Modal/Modal';
import Heading, { HeadingPosition, HeadingSize } from '../../shared/components/Typography/Heading/Heading';
import { Breakpoint, Screen, ScreenObserver } from '../ScreenObserver';
import { GoToInternationalStoreButton } from './GoToInternationalStoreButton/GoToInternationalStoreButton';
import { InitialStoreSelector } from './InitialStoreSelector/InitialStoreSelector';
import css from './InitialStoreSelectorModal.css';
import { InitialStoreSelectorModalState, ISelectItem } from './InitialStoreSelectorModalState';

interface InitialStoreSelectorModalProps {
  modal: InitialStoreSelectorModalState;
  translation: InitialStoreSelectorModalTranslation;
  image?: ImageData;
  videoUrl?: string;
}

interface InitialStoreSelectorModalComponentState {
  breakpoint: Breakpoint;
}

@observer
export class InitialStoreSelectorModal extends Component<
  InitialStoreSelectorModalProps,
  InitialStoreSelectorModalComponentState
> {
  constructor(props: InitialStoreSelectorModalProps) {
    super(props);
    this.state = { breakpoint: Breakpoint.Unknown };
  }

  componentDidMount(): void {
    this.onScreenChange(ScreenObserver.current);
    ScreenObserver.onScreenChange.subscribe(this.onScreenChange);
  }

  onScreenChange = (screen: Screen) => {
    this.setState({ breakpoint: screen.breakpoint });
  };

  handleOnSelect = (store: ISelectItem) => {
    this.props.modal.selectStore(store.value);
  };

  render() {
    const { modal, image, videoUrl, translation } = this.props;

    const showVideo = videoUrl && this.state.breakpoint === Breakpoint.Desktop;
    const showImage = image?.url && !showVideo;

    return (
      <Modal
        contentClassName={css.container}
        modal={modal}
        onApply={modal.apply}
        applyButtonDisabled={!modal.canApply}
        fullScreen
        hideCloseButton
        shouldNotCloseOnEsc
      >
        <div className={css.inner}>
          {showImage && <BackgroundImage src={image.url} alt={image.name} />}
          {showVideo && <BackgroundVideoPlayer sourceUrl={videoUrl} />}

          <div className={css.box}>
            <div className={css.boxInner}>
              <Logo className={css.logo} colorLight />
              <div className={css.textContainer}>
                <Heading
                  level={1}
                  className={css.title}
                  position={HeadingPosition.Center}
                  size={HeadingSize.ExtraLarge}
                >
                  {translation.title}
                </Heading>
                <p className={css.description}>{translation.description}</p>
              </div>
              <InitialStoreSelector modal={modal} translation={translation} onSelect={this.handleOnSelect} />
              <TertiaryButton onClick={modal.apply} className={css.buttonContinue}>
                {translation.buttonContinue}
              </TertiaryButton>
              <GoToInternationalStoreButton modal={modal} onSelect={this.handleOnSelect} translation={translation} />
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

export const CustomMenuListComponent = ({ children, ...props }: ComponentProps<typeof MenuListComponent>) => (
  <MenuListComponent {...props} className={css.CustomMenuListComponent}>
    {children}
  </MenuListComponent>
);
